import React, { useEffect, useRef, useState } from "react";
import "./HomePage.scss";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import axios from "axios";

type GalleryItem = {
  id?: number | string;
  title?: string;
  url: string;
  isVideo?: boolean;
  thumbnail_url?: string;
};

const OurGallery: React.FC = () => {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(3);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const token = localStorage.getItem("customer-token");
  const fetchingTenant = window.location.hostname.split(".")[0];
  const apiPort = process.env.REACT_APP_API_PROTOCOL;

  useEffect(() => {
    const updateSlides = () => {
      if (window.innerWidth < 600) setSlidesToShow(1);
      else if (window.innerWidth < 960) setSlidesToShow(2);
      else setSlidesToShow(3);
    };
    updateSlides();
    window.addEventListener("resize", updateSlides);
    return () => window.removeEventListener("resize", updateSlides);
  }, []);

  useEffect(() => {
    const fetchGallery = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/gc/gallery-media/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        const results = res?.data || [];
        console.log("Gallery results:", res);
        const normalized = results.map((it: any) => {
          const url: string = it.file || it.image || it.media_url || it.presigned_url;
          const isVideo = it.content_type.startsWith("video");
          return {
            id: it.id,
            title: it.title,
            url,
            isVideo,
            thumbnail_url: it.thumbnail_url,
          } as GalleryItem;
        });
        setItems(normalized.filter((g: GalleryItem) => !!g.url));
      } catch (e) {
        setError("Failed to load gallery");
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, [apiPort, fetchingTenant, token]);

  const totalSlides = items.length;
  const visibleSlides = Math.min(slidesToShow, totalSlides || 1);
  const maxIndex = Math.max(0, totalSlides - visibleSlides);

  // Clamp index when slides or data change
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [maxIndex, currentIndex]);

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (totalSlides <= 1) return; // nothing to slide
    if (playingIndex !== null) return;
    const id = setInterval(() => {
      setCurrentIndex((i) => (i < maxIndex ? i + 1 : 0));
    }, 5000);
    return () => clearInterval(id);
  }, [maxIndex, totalSlides, playingIndex]);

  const nextSlide = () => {
    if (currentIndex < Math.max(0, totalSlides - slidesToShow)) {
      setCurrentIndex((i) => i + 1);
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    }
  };

  if (loading) {
    return (
      <div className="carousel-container">
        <h2 className="carousel-title">Glimpse of Our Facilities</h2>
        <div className="no-content-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="carousel-container">
        <h2 className="carousel-title">Glimpse of Our Facilities</h2>
        <div className="no-content-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="carousel-container">
        <h2 className="carousel-title">Glimpse of Our Facilities</h2>
        <div className="no-content-container">
          <p>No gallery items available at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="carousel-container">
      <h2 className="carousel-title">Glimpse of Our Facilities</h2>

      <div className="carousel-wrapper">
        <button className="nav left" onClick={prevSlide} disabled={currentIndex === 0}>
          <ChevronLeftIcon />
        </button>

        <div
          className="carousel-track"
          style={{ transform: `translateX(-${currentIndex * (100 / visibleSlides)}%)` }}
        >
          {items.map((item: GalleryItem, idx: number) => (
            <div
              key={item?.id || idx}
              className="carousel-card"
              style={{ flex: `0 0 ${100 / slidesToShow}%` }}
            >
              <div className="media-container">
                {item.isVideo ? (
                  <div style={{ position: "relative", width: "100%", height: "100%" }}>
                    <video
                      ref={el => (videoRefs.current[idx] = el)}
                      className="media-content"
                      controls
                      preload="metadata"
                      poster={item.thumbnail_url || ""}
                      playsInline
                      style={{ width: "100%", height: "100%", objectFit: "contain", cursor: "pointer" }}
                      onClick={(e) => e.stopPropagation()}
                      onPlay={() => setPlayingIndex(idx)}
                      onPause={() => setPlayingIndex(null)}
                      onEnded={() => setPlayingIndex(null)}
                    >
                      <source src={item.url} type="video/mp4" />
                    </video>
                    {playingIndex !== idx && (
                      <div 
                      className="video-overlay"
                      onClick={(e) => {
                        e.stopPropagation();
                        videoRefs.current.forEach(v => v?.pause());
                        setPlayingIndex(idx);
                        videoRefs.current[idx]?.play();
                      }}>
                      <PlayArrowIcon className="play-icon" />
                    </div>
                    )}
                  </div>
                ) : (
                  <img
                    className="media-content"
                    src={item.url}
                    alt={item?.title || `Gallery ${idx + 1}`}
                    loading="lazy"
                    style={{ width: "100%", height: "100%", objectFit: "contain", cursor: "pointer" }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        <button
          className="nav right"
          onClick={nextSlide}
          disabled={currentIndex >= maxIndex}
        >
          <ChevronRightIcon />
        </button>
      </div>

      <div className="dots">
        {Array.from({ length: Math.max(1, totalSlides - slidesToShow + 1) }).map((_, i) => (
          <span
            key={i}
            className={`dot ${i === currentIndex ? "active" : ""}`}
            onClick={() => setCurrentIndex(i)}
          />
        ))}
      </div>
    </div>
  );
};

export default OurGallery;